import { useCallback } from 'react';
import { useInAppBrowser } from 'App/capacitor';
import { useNavegate } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import { logError } from 'commons/logger';
import usePayorderToPay from './usePayorderToPay';
import isValidCallbackDomain from './utils/isValidCallbackDomain';
import { BILLING_ROUTES } from '../../billingRoutes';
import { TRANSACTION_FEES_ROUTES } from '../../TransactionFees/transactionFeesRoutes';
import { cleanFetchTransactionFeeToPay } from '../../TransactionFees/TransactionFeesSlice';
import {
  AFTER_PAY_ACTION_QUERY,
  AFTER_PAY_CALLBACK_QUERY,
  CHARGE_PAID_QUERY,
  CHECKOUT_ROUTES,
  CONCEPT_CODE_PAID_QUERY,
} from '../checkoutRoutes';

export const GO_TO_PLANS_AFTER_PAYMENT = 'plans';
export const GO_TO_TRANSACTION_FEES_AFTER_PAYMENT = 'transaction-fee';
export const GO_TO_CHECKOUT_AFTER_PAYMENT = 'checkout';

const AFTER_PAY_ROUTES = {
  [GO_TO_PLANS_AFTER_PAYMENT]: BILLING_ROUTES.plansFromUpgrade,
  [GO_TO_TRANSACTION_FEES_AFTER_PAYMENT]:
    TRANSACTION_FEES_ROUTES.transactionFees,
  [GO_TO_CHECKOUT_AFTER_PAYMENT]: CHECKOUT_ROUTES.plansAndSubscriptions,
};

export type CompletedPaymentAction =
  | typeof GO_TO_PLANS_AFTER_PAYMENT
  | typeof GO_TO_CHECKOUT_AFTER_PAYMENT
  | typeof GO_TO_TRANSACTION_FEES_AFTER_PAYMENT;

interface UseAfterPaymentRedirection {
  onCompletedPayment: () => void;
  addAfterPayActionToPath: (
    path: string,
    action?: CompletedPaymentAction,
  ) => string;
  fullPath: string;
  afterPaySetting: string | null;
  passOnActionToPath: (path: string) => string;
}

function addFromPaymentQuery(route: string) {
  return `${route}${route.includes('?') ? '&' : '?'}fromPayment`;
}

/**
 * Provides necessary functionality for setting and interpreting the "afterPay" search param in the
 * checkout process.
 *
 * This parameter tells us where to redirect the user after a successful payment.
 */
function useAfterPaymentRedirection(): UseAfterPaymentRedirection {
  const dispatch = useAppDispatch();
  const { getQueryParam, goTo, currentRoot } = useNavegate();
  const openInAppBrowser = useInAppBrowser();
  const { mainConcept, payorderToPay } = usePayorderToPay();
  const afterPaySetting = getQueryParam(
    AFTER_PAY_ACTION_QUERY,
  ) as CompletedPaymentAction;
  const callbackUrl = getQueryParam(AFTER_PAY_CALLBACK_QUERY);

  const route = addFromPaymentQuery(
    AFTER_PAY_ROUTES[afterPaySetting ?? ''] ?? CHECKOUT_ROUTES.thankYouPage,
  );

  const extraAction = useCallback(() => {
    switch (afterPaySetting) {
      case GO_TO_TRANSACTION_FEES_AFTER_PAYMENT:
        dispatch(cleanFetchTransactionFeeToPay());
        break;
    }
  }, [afterPaySetting, dispatch]);

  const getCallbackUrl = useCallback(() => {
    if (!callbackUrl) {
      return;
    }

    try {
      const url = new URL(decodeURIComponent(callbackUrl));

      if (isValidCallbackDomain(url.hostname)) {
        return addFromPaymentQuery(url.href);
      }
    } catch (err) {
      logError('Failed to parse callbackUrl', err);
    }
  }, [callbackUrl]);

  const fullPath = getCallbackUrl() ?? currentRoot + route;

  const addAfterPayActionToPath = useCallback(
    (path: string, action?: CompletedPaymentAction) => {
      const symbol = path.includes('?') ? '' : '?';

      const actionString = action ? `&${AFTER_PAY_ACTION_QUERY}=${action}` : '';

      const conceptCodePaidString = mainConcept
        ? `&${CONCEPT_CODE_PAID_QUERY}=${mainConcept}`
        : '';

      const chargePaidString = payorderToPay?.charges[0]
        ? `&${CHARGE_PAID_QUERY}=${payorderToPay?.charges[0].id}`
        : '';

      return path
        .concat(
          `${symbol}${actionString}${conceptCodePaidString}${chargePaidString}`,
        )
        .replaceAll('?&', '?');
    },
    [mainConcept, payorderToPay],
  );

  const passOnActionToPath = useCallback(
    (path: string): string => {
      if (callbackUrl) {
        const [pathname, search] = path.split('?');
        const params = new URLSearchParams(search);
        params.set(AFTER_PAY_CALLBACK_QUERY, callbackUrl);

        return `${pathname}?${params.toString()}`;
      }

      return addAfterPayActionToPath(path, afterPaySetting);
    },
    [afterPaySetting, callbackUrl, addAfterPayActionToPath],
  );

  const onCompletedPayment = useCallback(() => {
    const redirect = async (url: string) => {
      await openInAppBrowser(url);
    };

    const callbackUrl = getCallbackUrl();
    if (callbackUrl) {
      redirect(callbackUrl);
    } else {
      goTo(passOnActionToPath(route));
    }

    extraAction();
  }, [
    extraAction,
    getCallbackUrl,
    goTo,
    route,
    passOnActionToPath,
    openInAppBrowser,
  ]);

  return {
    onCompletedPayment,
    addAfterPayActionToPath,
    fullPath,
    afterPaySetting,
    passOnActionToPath,
  };
}

export default useAfterPaymentRedirection;
